/**
 * ShopScreen — màn gameplay chính (UC-05 + UC-06 + UC-08).
 *
 * Wire 5 components B7:
 *   - HUD (Bean/Reputation/Energy) + BeanCounter cho Bean.
 *   - RevenueTicker.
 *   - CustomerCard với dialogue + patience.
 *   - IngredientButton × 4.
 *   - CoinFloater queue.
 *
 * State: 2 chế độ
 *   A) Chưa có session active → nút "Mở quán".
 *   B) Có session active + currentOrder → gameplay loop.
 *
 * Phase 1: KHÔNG có UpgradeScreen (B9). KHÔNG có MilestoneToast UI (B9 — chỉ
 * log analytics ở đây).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { BeanCounter } from "../components/BeanCounter";
import { CoinFloater } from "../components/CoinFloater";
import { CustomerCard } from "../components/CustomerCard";
import { IngredientButton } from "../components/IngredientButton";
import { RevenueTicker } from "../components/RevenueTicker";
import { extractErrorMessage } from "../lib/api";
import { track } from "../lib/analytics";
import { playSound, toggleMuted, isMuted, unlock } from "../lib/sound";
import { useShopStore } from "../stores/shopStore";
import type { FloatingCoin, IngredientCode, RecipeCode } from "../types";

const ALL_INGREDIENTS: IngredientCode[] = ["coffee", "milk", "ice", "sugar"];

/** Tên món tiếng Việt — KHÔNG phụ thuộc backend name_key. */
const RECIPE_LABEL_VI: Record<RecipeCode, string> = {
  ca_phe_den_da: "Cà phê đen đá",
  ca_phe_sua_da: "Cà phê sữa đá",
  bac_xiu: "Bạc xỉu",
};

/** Công thức đúng để hiển thị hint khi user pha sai. */
const RECIPE_HINT_VI: Record<RecipeCode, string> = {
  ca_phe_den_da: "Cà phê + Đá",
  ca_phe_sua_da: "Cà phê + Sữa + Đá",
  bac_xiu: "Cà phê + Sữa + Đá",
};

export function ShopScreen() {
  const navigate = useNavigate();
  const shop = useShopStore((s) => s.shop);
  const session = useShopStore((s) => s.session);
  const currentOrder = useShopStore((s) => s.currentOrder);
  const revenue = useShopStore((s) => s.revenue);
  const floatingCoins = useShopStore((s) => s.floatingCoins);
  const startSession = useShopStore((s) => s.startSession);
  const serveOrder = useShopStore((s) => s.serveOrder);
  const endSession = useShopStore((s) => s.endSession);
  const pushFloatingCoin = useShopStore((s) => s.pushFloatingCoin);
  const removeFloatingCoin = useShopStore((s) => s.removeFloatingCoin);
  const loadShop = useShopStore((s) => s.loadShop);
  const loadActiveSession = useShopStore((s) => s.loadActiveSession);

  const [selected, setSelected] = useState<IngredientCode[]>([]);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [muted, setMutedUI] = useState<boolean>(() => isMuted());
  const [hasFirstOrder, setHasFirstOrder] = useState(false);
  const [endSummary, setEndSummary] = useState<{
    total_orders: number;
    total_correct: number;
    total_bean_earned: number;
    duration_seconds: number;
  } | null>(null);
  // Modal acknowledge khi pha sai — block UI cho đến khi user dismiss.
  const [wrongServeInfo, setWrongServeInfo] = useState<{
    recipe: RecipeCode;
    selected: IngredientCode[];
    beanPenalty: number;
  } | null>(null);

  useEffect(() => {
    if (!shop) {
      // Reload tab → load shop + check session active để resume.
      loadActiveSession()
        .catch(() => loadShop())
        .catch(() => navigate("/login"));
    } else if (shop.name === null) {
      navigate("/onboarding/intro");
    } else if (!session) {
      // Có shop + có name nhưng chưa load session active.
      loadActiveSession().catch(() => {
        // Không có session active = OK, UI sẽ show nút "Mở quán".
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop?.id]);

  function selectedCount(code: IngredientCode): number {
    return selected.filter((c) => c === code).length;
  }

  function clearFeedback() {
    setTimeout(() => setFeedback(null), 2500);
  }

  async function onStart() {
    unlock();
    setBusy(true);
    setFeedback(null);
    try {
      await startSession();
      setSelected([]);
      playSound("ingredient_click");
    } catch (err) {
      // Nếu backend báo SESSION_ALREADY_ACTIVE → resume session đó.
      try {
        await loadActiveSession();
        setSelected([]);
        setFeedback("Đã khôi phục ca đang mở");
        clearFeedback();
      } catch {
        setFeedback(extractErrorMessage(err));
        clearFeedback();
      }
    } finally {
      setBusy(false);
    }
  }

  async function onServe() {
    if (!currentOrder || busy) return;
    unlock();
    setBusy(true);
    setFeedback(null);
    // Lưu lại order user vừa pha để show hint nếu sai.
    const servedRecipe = currentOrder.requested_recipe;
    try {
      const result = await serveOrder(selected);

      const startX = typeof window !== "undefined" ? window.innerWidth / 2 : 200;
      const startY = typeof window !== "undefined" ? window.innerHeight / 2 : 300;

      if (result.is_correct && !result.timed_out) {
        // ─── ĐÚNG MÓN ───
        // Coin bay vàng/cam (bean + tip nếu có).
        const coins: FloatingCoin[] = [
          {
            id: crypto.randomUUID(),
            amount: result.bean_earned,
            type: "bean",
            startX,
            startY,
          },
        ];
        if (result.tip_earned > 0) {
          coins.push({
            id: crypto.randomUUID(),
            amount: result.tip_earned,
            type: "tip",
            startX: startX + 40,
            startY,
          });
        }
        for (const c of coins) {
          pushFloatingCoin(c);
          setTimeout(() => removeFloatingCoin(c.id), 1300);
        }
        playSound("serve_success");
        setTimeout(() => playSound("revenue_ting"), 220);
        setFeedback(`Tuyệt vời! +${result.bean_earned + result.tip_earned} ☕`);
        if (!hasFirstOrder) {
          setHasFirstOrder(true);
          track("first_order_completed");
        }
      } else if (result.timed_out) {
        // ─── KHÁCH BỎ ĐI ───
        setFeedback("Khách bỏ đi 😢 — pha nhanh hơn nhé");
        clearFeedback();
      } else {
        // ─── SAI MÓN ───
        // KHÔNG bay coin to. Mở modal block UI để user thực sự ack.
        setWrongServeInfo({
          recipe: servedRecipe,
          selected: [...selected],
          beanPenalty: result.bean_earned,
        });
        // KHÔNG clearFeedback ở đây vì modal sẽ giữ; toast nếu có cũng tự fade.
      }
      setSelected([]);
    } catch (err) {
      setFeedback(extractErrorMessage(err));
      clearFeedback();
    } finally {
      setBusy(false);
    }
  }

  async function onEndSession() {
    setBusy(true);
    try {
      const summary = await endSession();
      track("session_ended", { ...summary });
      setEndSummary(summary);
      setSelected([]);
    } catch (err) {
      setFeedback(extractErrorMessage(err));
      clearFeedback();
    } finally {
      setBusy(false);
    }
  }

  if (!shop) {
    return (
      <main className="min-h-full flex items-center justify-center text-coffee/70">
        Đang tải quán…
      </main>
    );
  }

  return (
    <main className="min-h-full relative">
      {/* Background quán — overlay mờ để UI nổi */}
      <div
        aria-hidden="true"
        className="fixed inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url(/assets/shop_background.jpg)" }}
      />
      <div
        aria-hidden="true"
        className="fixed inset-0 bg-gradient-to-b from-cream/85 via-cream/75 to-cream/95"
      />

      <div className="relative max-w-2xl mx-auto p-4 flex flex-col gap-3">
      {/* HUD */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white rounded-xl p-2 shadow-soft-amber">
          <div className="text-xs text-coffee/60">Bean</div>
          <BeanCounter value={shop.bean_coin} size="medium" />
        </div>
        <div className="bg-white rounded-xl p-2 shadow-soft-amber">
          <div className="text-xs text-coffee/60">Danh tiếng</div>
          <div className="text-2xl font-bold text-coffee">
            ⭐ {shop.reputation}
          </div>
        </div>
        <div className="bg-white rounded-xl p-2 shadow-soft-amber">
          <div className="text-xs text-coffee/60">Năng lượng</div>
          <div className="text-2xl font-bold text-coffee">
            ⚡ {shop.energy}/{shop.energy_max}
          </div>
        </div>
      </div>

      {/* Top bar: shop name + mute */}
      <div className="flex items-center justify-between text-sm">
        <div className="text-coffee/70">
          Quán <span className="font-semibold text-coffee">{shop.name}</span>
        </div>
        <button
          type="button"
          onClick={() => {
            const next = toggleMuted();
            setMutedUI(next);
          }}
          className="text-xs px-2 py-1 rounded-md bg-white border border-coffee/15"
          data-testid="mute-toggle"
        >
          {muted ? "🔇 Tắt tiếng" : "🔊 Bật tiếng"}
        </button>
      </div>

      {/* Revenue */}
      {revenue && (
        <RevenueTicker
          sessionRevenue={revenue.session_revenue}
          todayRevenue={revenue.today_revenue}
          shopBalance={revenue.shop_balance}
          yesterdayDeltaPct={revenue.today_revenue_delta_pct}
          todayOrders={revenue.today_orders}
        />
      )}

      {/* Gameplay area */}
      {endSummary ? (
        <div className="bg-white rounded-2xl p-6 shadow-soft-amber text-center mt-4">
          <div className="text-5xl mb-2">🛍</div>
          <h2 className="text-xl font-bold text-coffee mb-2">Kết thúc ca!</h2>
          <p className="text-sm text-coffee/70 mb-3">
            Ca này bán {endSummary.total_orders} đơn, đúng{" "}
            {endSummary.total_correct}.
          </p>
          <p className="text-coffee font-semibold">
            ☕ {endSummary.total_bean_earned.toLocaleString("vi-VN")} Bean ·{" "}
            {endSummary.duration_seconds}s
          </p>
          <button
            onClick={() => {
              setEndSummary(null);
            }}
            className="mt-4 w-full py-2.5 rounded-lg bg-coffee text-white font-semibold"
          >
            Đóng
          </button>
        </div>
      ) : !session ? (
        <div className="bg-white rounded-2xl p-6 shadow-soft-amber text-center mt-4">
          <div className="text-5xl mb-2">🚪</div>
          <h2 className="text-xl font-bold text-coffee mb-2">Quán đang đóng</h2>
          <p className="text-sm text-coffee/70 mb-4">
            Bấm "Mở quán" để bắt đầu ca bán hàng. Mỗi ca tốn 1 Energy.
          </p>
          <button
            type="button"
            onClick={onStart}
            disabled={busy || shop.energy < 1}
            className="w-full py-3 rounded-lg bg-coffee text-white font-bold disabled:opacity-60"
            data-testid="open-shop"
          >
            {busy
              ? "Đang mở…"
              : shop.energy < 1
                ? "Hết Energy — thử lại sau"
                : "Mở quán"}
          </button>
        </div>
      ) : currentOrder ? (
        <>
          <div className="mt-2">
            <CustomerCard
              customerType={currentOrder.customer_type}
              dialogue={currentOrder.customer_dialogue}
              requestedRecipeDisplay={RECIPE_LABEL_VI[currentOrder.requested_recipe]}
              patienceDeadline={currentOrder.patience_deadline}
            />
          </div>

          {/* Ingredient grid */}
          <div className="grid grid-cols-4 gap-2 mt-3">
            {ALL_INGREDIENTS.map((ing) => (
              <IngredientButton
                key={ing}
                code={ing}
                selectedCount={selectedCount(ing)}
                onClick={(code) => {
                  unlock();
                  playSound("ingredient_click");
                  setSelected((s) => [...s, code]);
                }}
                disabled={busy}
              />
            ))}
          </div>

          {/* Selected preview */}
          <div className="bg-white rounded-xl p-3 shadow-soft-amber min-h-[3rem] flex items-center justify-between">
            <div className="text-sm text-coffee/80">
              <span className="text-coffee/60">Ly đang pha:</span>{" "}
              {selected.length === 0
                ? "—"
                : selected
                    .map((c) => labelOf(c))
                    .join(" + ")}
            </div>
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => setSelected([])}
                className="text-xs text-stool underline"
                data-testid="clear-selected"
              >
                Xoá
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={onServe}
            disabled={busy || selected.length === 0}
            className="w-full py-3 rounded-2xl bg-coffee text-white text-lg font-bold shadow-soft-amber disabled:opacity-60"
            data-testid="serve-button"
          >
            {busy ? "Đang phục vụ…" : "PHỤC VỤ"}
          </button>

          <button
            type="button"
            onClick={onEndSession}
            disabled={busy}
            className="text-sm text-coffee/60 underline mt-1"
            data-testid="end-session"
          >
            Kết thúc ca
          </button>
        </>
      ) : (
        <div className="bg-white rounded-2xl p-6 shadow-soft-amber text-center mt-4">
          <p className="text-coffee/70">Đang spawn khách kế tiếp…</p>
        </div>
      )}

      {feedback && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-coffee text-white px-4 py-2 rounded-full text-sm shadow-soft-amber z-40"
          data-testid="feedback-toast"
        >
          {feedback}
        </div>
      )}

      {/* Modal "Pha sai!" — block UI cho đến khi user click "Đã hiểu". */}
      {wrongServeInfo && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          data-testid="wrong-serve-modal"
        >
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl border-4 border-stool/50 animate-[wiggle_0.3s_ease-in-out]">
            <div className="text-6xl text-center mb-2">❌</div>
            <h2 className="text-2xl font-bold text-stool text-center mb-1">
              Pha sai rồi!
            </h2>
            <p className="text-center text-coffee/70 text-sm mb-4">
              Khách không hài lòng. Bạn chỉ nhận {wrongServeInfo.beanPenalty} ☕
              an ủi.
            </p>

            <div className="space-y-3 bg-cream/60 rounded-xl p-4 text-sm">
              <div>
                <div className="text-coffee/60 text-xs mb-1">Bạn đã pha:</div>
                <div className="font-semibold text-stool">
                  {wrongServeInfo.selected.length === 0
                    ? "(không chọn gì)"
                    : wrongServeInfo.selected.map(labelOf).join(" + ")}
                </div>
              </div>
              <div>
                <div className="text-coffee/60 text-xs mb-1">
                  Khách yêu cầu:{" "}
                  <span className="font-bold">
                    {RECIPE_LABEL_VI[wrongServeInfo.recipe]}
                  </span>
                </div>
                <div className="font-semibold text-leaf">
                  Cần pha: {RECIPE_HINT_VI[wrongServeInfo.recipe]}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setWrongServeInfo(null)}
              className="mt-5 w-full py-3 rounded-xl bg-coffee text-white font-bold shadow-soft-amber"
              data-testid="wrong-serve-ack"
              autoFocus
            >
              Đã hiểu, thử lại
            </button>
          </div>
        </div>
      )}

      <CoinFloater coins={floatingCoins} />
      </div>
    </main>
  );
}

function labelOf(code: IngredientCode): string {
  return {
    coffee: "Cà phê",
    milk: "Sữa",
    ice: "Đá",
    sugar: "Đường",
  }[code];
}
